'use client'

import {
    FinanceOperator,
    getEligibleUsers,
    getOperators,
    setOperators,
} from '@/lib/api/financial/digital-account-api'
import {
    getPaymentAccount,
    setAllowSelfApproval,
} from '@/lib/api/financial/payment-account-api'
import { useEffect, useMemo, useState } from 'react'
import { MdSearch, MdShield } from 'react-icons/md'
import { toast } from 'react-toastify'
import PasswordField from './password-field'

export default function OperatorsModal({ onClose }: { onClose: () => void }) {
    const [eligible, setEligible] = useState<FinanceOperator[]>([])
    const [selected, setSelected] = useState<number[]>([])
    // Snapshot dos operadores aprovados no momento do load — usado pra ORDEM
    // estavel da lista (aprovados em cima). Nao muda quando o user toggla,
    // entao desmarcar um operador nao faz ele "pular" pro fim.
    const [initiallyApproved, setInitiallyApproved] = useState<Set<number>>(
        new Set()
    )
    const [password, setPassword] = useState('')
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [allowSelfApproval, setAllowSelfApprovalState] = useState(false)
    const [savingSelfApproval, setSavingSelfApproval] = useState(false)

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        const base = q
            ? eligible.filter(
                  u =>
                      u.name.toLowerCase().includes(q) ||
                      u.email.toLowerCase().includes(q) ||
                      u.userType.toLowerCase().includes(q)
              )
            : eligible
        // Aprovados (no snapshot inicial) vem primeiro, mantendo ordem alfabetica
        // dentro de cada grupo.
        return [...base].sort((a, b) => {
            const aApproved = initiallyApproved.has(a.id) ? 0 : 1
            const bApproved = initiallyApproved.has(b.id) ? 0 : 1
            if (aApproved !== bApproved) return aApproved - bApproved
            return a.name.localeCompare(b.name, 'pt-BR')
        })
    }, [eligible, search, initiallyApproved])

    useEffect(() => {
        ;(async () => {
            setLoading(true)
            try {
                const [ops, all, acc] = await Promise.all([
                    getOperators(),
                    getEligibleUsers(),
                    getPaymentAccount(),
                ])
                if (ops.success && ops.result) {
                    setSelected(ops.result.userIds)
                    setInitiallyApproved(new Set(ops.result.userIds))
                }
                if (all.success && all.result) setEligible(all.result)
                if (acc.success && acc.result)
                    setAllowSelfApprovalState(!!acc.result.allowSelfApproval)
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const handleToggleSelfApproval = async (allow: boolean) => {
        setSavingSelfApproval(true)
        // Optimistic update — reverte em caso de erro.
        const previous = allowSelfApproval
        setAllowSelfApprovalState(allow)
        try {
            const res = await setAllowSelfApproval(allow)
            if (res.success) {
                toast.success(
                    allow
                        ? 'Auto-aprovação habilitada'
                        : 'Auto-aprovação desabilitada'
                )
            } else {
                setAllowSelfApprovalState(previous)
                toast.error(res.error || 'Erro ao atualizar')
            }
        } finally {
            setSavingSelfApproval(false)
        }
    }

    const toggle = (id: number) => {
        setSelected(s =>
            s.includes(id) ? s.filter(x => x !== id) : [...s, id]
        )
    }

    const handleSave = async () => {
        if (!password) return toast.warning('Confirme sua senha')
        setSaving(true)
        try {
            const res = await setOperators(selected, password)
            if (res.success) {
                toast.success('Operadores atualizados')
                onClose()
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                    <MdShield className="text-amber-500" /> Operadores
                    financeiros
                </h3>
                <p className="text-xs text-base-content/60 mb-4">
                    Enquanto nenhum operador está marcado, qualquer usuário ADM
                    pode movimentar a conta. Após marcar pelo menos um, apenas
                    operadores eleitos poderão movimentar e alterar esta lista.
                </p>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-md text-secondary" />
                    </div>
                ) : eligible.length === 0 ? (
                    <p className="text-sm text-base-content/60 text-center py-6">
                        Nenhum usuário ativo encontrado para esta empresa.
                    </p>
                ) : (
                    <>
                        <div className="relative mb-2">
                            <MdSearch className="absolute z-10 left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 text-sm" />
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full pl-8"
                                placeholder="Buscar por nome, e-mail ou tipo"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                        {filtered.length === 0 ? (
                            <p className="text-sm text-base-content/60 text-center py-6">
                                Nenhum resultado para "{search}".
                            </p>
                        ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto border rounded-lg p-2">
                                {filtered.map(u => (
                                    <label
                                        key={u.id}
                                        className="flex items-center gap-3 p-2 rounded hover:bg-base-200 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm"
                                            checked={selected.includes(u.id)}
                                            onChange={() => toggle(u.id)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">
                                                {u.name}
                                            </p>
                                            <p className="text-xs text-base-content/60 truncate">
                                                {u.email} ·{' '}
                                                <span className="badge badge-ghost badge-xs">
                                                    {u.userType}
                                                </span>
                                            </p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {!loading && (
                    <div className="mt-4 p-3 bg-base-200 rounded-lg">
                        <label className="flex items-start justify-between gap-3 cursor-pointer">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">
                                    Permitir auto-aprovação
                                </p>
                                <p className="text-xs text-base-content/60">
                                    Se ativo, o mesmo usuário que solicita uma
                                    transferência pode aprová-la. Quando
                                    desativado, é exigido que outro usuário
                                    aprove (separação de funções).
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={allowSelfApproval}
                                onChange={e =>
                                    handleToggleSelfApproval(e.target.checked)
                                }
                                disabled={savingSelfApproval}
                            />
                        </label>
                    </div>
                )}

                <div className="mt-4">
                    <PasswordField
                        value={password}
                        onChange={setPassword}
                        disabled={saving}
                    />
                </div>

                <div className="modal-action">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            'Salvar operadores'
                        )}
                    </button>
                </div>
            </div>
            <div
                className="modal-backdrop"
                onClick={() => !saving && onClose()}
            />
        </div>
    )
}
